import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  getAllNextActions,
  updateNextActionStatus,
} from "../services/firestore";
import { formatDate } from "../utils";
import "./NextActionsView.css";

interface NextActionItem {
  artworkId: string;
  artworkTitle: string;
  artworkArtist: string;
  artworkLocation: string;
  artworkStatus: string;
  operationId: string;
  operationType: string;
  operationDate: Date;
  nextAction: string;
  nextActionStatus: "pending" | "completed" | "cancelled";
  nextActionCompletedAt?: Date;
  nextActionCompletionNotes?: string;
  operationNotes?: string;
  priority: "high" | "medium" | "low";
  daysOverdue?: number;
}

const NextActionsView: React.FC = () => {
  const [nextActions, setNextActions] = useState<NextActionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterPriority, setFilterPriority] = useState<string>("all");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterActionStatus, setFilterActionStatus] = useState<string>("all"); // ✅ NUOVO FILTRO
  const [filterArtworkStatus, setFilterArtworkStatus] = useState<string>("all");
  const [sortBy, setSortBy] = useState<
    "date" | "priority" | "overdue" | "artwork" | "status"
  >("date");
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);

  useEffect(() => {
    fetchNextActions();
  }, []);

  const fetchNextActions = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log("📋 Fetching next actions...");
      const rawActions = await getAllNextActions();

      const processedActions: NextActionItem[] = rawActions.map((action) => {
        const daysSinceOperation = Math.floor(
          (new Date().getTime() - action.operationDate.getTime()) /
            (1000 * 60 * 60 * 24)
        );

        const priority = determinePriority(
          action.operationType,
          daysSinceOperation,
          action.nextActionStatus
        );

        return {
          ...action,
          priority,
          daysOverdue:
            daysSinceOperation > 7 ? daysSinceOperation - 7 : undefined,
        };
      });

      setNextActions(processedActions);
      console.log(`✅ Processed ${processedActions.length} next actions`);
    } catch (error) {
      console.error("❌ Error fetching next actions:", error);
      setError("Failed to load next actions. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const determinePriority = (
    operationType: string,
    daysSince: number,
    status: "pending" | "completed" | "cancelled"
  ): "high" | "medium" | "low" => {
    // Le azioni completate o cancellate hanno priorità bassa
    if (status === "completed" || status === "cancelled") return "low";

    // Alta priorità: azioni pending vecchie di più di 14 giorni
    if (daysSince > 14) return "high";

    // Media priorità: azioni 7-14 giorni fa, o chiamate/email urgenti
    if (daysSince > 7) return "medium";
    if (operationType === "call" || operationType === "email") return "medium";

    return "low";
  };

  // ✅ NUOVA FUNZIONE per aggiornare lo stato di una next action
  const handleStatusChange = async (
    artworkId: string,
    operationId: string,
    newStatus: "pending" | "completed" | "cancelled"
  ) => {
    try {
      setUpdatingStatus(`${artworkId}-${operationId}`);

      const completionNotes =
        newStatus === "completed"
          ? prompt("Optional completion notes:") || undefined
          : undefined;

      await updateNextActionStatus(
        artworkId,
        operationId,
        newStatus,
        completionNotes
      );

      // Ricarica i dati
      await fetchNextActions();

      console.log(`✅ Status updated to ${newStatus}`);
    } catch (error) {
      console.error("❌ Error updating status:", error);
      alert("Failed to update status. Please try again.");
    } finally {
      setUpdatingStatus(null);
    }
  };

  const getFilteredAndSortedActions = () => {
    let filtered = [...nextActions];

    // Filtro per priorità
    if (filterPriority !== "all") {
      filtered = filtered.filter(
        (action) => action.priority === filterPriority
      );
    }

    // Filtro per timing overdue
    if (filterStatus === "overdue") {
      filtered = filtered.filter((action) => action.daysOverdue);
    } else if (filterStatus === "recent") {
      filtered = filtered.filter((action) => !action.daysOverdue);
    }

    // ✅ NUOVO FILTRO per stato next action
    if (filterActionStatus !== "all") {
      filtered = filtered.filter(
        (action) => action.nextActionStatus === filterActionStatus
      );
    }

    // Filtro per stato artwork
    if (filterArtworkStatus !== "all") {
      filtered = filtered.filter(
        (action) => action.artworkStatus === filterArtworkStatus
      );
    }

    // Ordinamento
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "priority":
          const priorityOrder = { high: 3, medium: 2, low: 1 };
          const priorityDiff =
            priorityOrder[b.priority] - priorityOrder[a.priority];
          return priorityDiff !== 0
            ? priorityDiff
            : b.operationDate.getTime() - a.operationDate.getTime();

        case "status":
          const statusOrder = { pending: 3, cancelled: 2, completed: 1 };
          const statusDiff =
            statusOrder[b.nextActionStatus] - statusOrder[a.nextActionStatus];
          return statusDiff !== 0
            ? statusDiff
            : b.operationDate.getTime() - a.operationDate.getTime();

        case "overdue":
          const aOverdue = a.daysOverdue || 0;
          const bOverdue = b.daysOverdue || 0;
          return bOverdue !== aOverdue
            ? bOverdue - aOverdue
            : b.operationDate.getTime() - a.operationDate.getTime();

        case "artwork":
          const artworkDiff = a.artworkTitle.localeCompare(b.artworkTitle);
          return artworkDiff !== 0
            ? artworkDiff
            : b.operationDate.getTime() - a.operationDate.getTime();

        case "date":
        default:
          return b.operationDate.getTime() - a.operationDate.getTime();
      }
    });

    return filtered;
  };

  const filteredActions = getFilteredAndSortedActions();

  // Funzioni di utilità per lo styling
  const getStatusColor = (status: "pending" | "completed" | "cancelled") => {
    switch (status) {
      case "pending":
        return "#ffc107";
      case "completed":
        return "#28a745";
      case "cancelled":
        return "#6c757d";
      default:
        return "#6c757d";
    }
  };

  const getStatusIcon = (status: "pending" | "completed" | "cancelled") => {
    switch (status) {
      case "pending":
        return "⏳";
      case "completed":
        return "✅";
      case "cancelled":
        return "❌";
      default:
        return "❓";
    }
  };

  // Statistiche aggiornate
  const stats = {
    total: nextActions.length,
    pending: nextActions.filter((a) => a.nextActionStatus === "pending").length,
    completed: nextActions.filter((a) => a.nextActionStatus === "completed")
      .length,
    cancelled: nextActions.filter((a) => a.nextActionStatus === "cancelled")
      .length,
    overdue: nextActions.filter(
      (a) => a.daysOverdue && a.nextActionStatus === "pending"
    ).length,
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "high":
        return "#dc3545";
      case "medium":
        return "#ffc107";
      case "low":
        return "#28a745";
      default:
        return "#6c757d";
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case "high":
        return "🔴";
      case "medium":
        return "🟡";
      case "low":
        return "🟢";
      default:
        return "⚪";
    }
  };

  const getOperationIcon = (type: string) => {
    switch (type) {
      case "call":
        return "📞";
      case "email":
        return "✉️";
      case "visit":
        return "🏛️";
      case "status_change":
        return "🔄";
      case "note_added":
        return "📝";
      case "digitization":
        return "📷";
      case "research":
        return "🔍";
      case "meeting":
        return "🤝";
      default:
        return "🔧";
    }
  };

  const getArtworkStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "#6c757d";
      case "in-progress":
        return "#007bff";
      case "completed":
        return "#28a745";
      case "obtained":
        return "#000000";
      default:
        return "#6c757d";
    }
  };

  return (
    <div className="next-actions-container">
      {/* Header */}
      <div className="next-actions-header">
        <div className="next-actions-header-content">
          <h1>
            <span className="next-actions-header-icon">⏭️</span>
            Next Actions
          </h1>
          <p>
            Track and manage all pending follow-up actions from your artwork
            operations
          </p>
        </div>

        <button
          onClick={fetchNextActions}
          className="next-actions-refresh-button"
          disabled={loading}
        >
          {loading ? (
            <>
              <div className="next-actions-refresh-spinner"></div>
              Loading...
            </>
          ) : (
            <>🔄 Refresh</>
          )}
        </button>
      </div>

      {/* Error State */}
      {error && (
        <div className="next-actions-error">
          <div className="next-actions-error-icon">⚠️</div>
          <div className="next-actions-error-content">
            <h3>Error Loading Next Actions</h3>
            <p>{error}</p>
            <button
              onClick={fetchNextActions}
              className="next-actions-error-retry"
            >
              🔄 Try Again
            </button>
          </div>
        </div>
      )}

      {/* Statistics - AGGIORNATE */}
      {!error && (
        <div className="next-actions-stats">
          <div className="next-actions-stat-card total">
            <div className="next-actions-stat-icon">📊</div>
            <div className="next-actions-stat-content">
              <div className="next-actions-stat-number">{stats.total}</div>
              <div className="next-actions-stat-label">Total Actions</div>
            </div>
          </div>

          <div className="next-actions-stat-card pending">
            <div className="next-actions-stat-icon">⏳</div>
            <div className="next-actions-stat-content">
              <div className="next-actions-stat-number">{stats.pending}</div>
              <div className="next-actions-stat-label">Pending</div>
            </div>
          </div>

          <div className="next-actions-stat-card completed">
            <div className="next-actions-stat-icon">✅</div>
            <div className="next-actions-stat-content">
              <div className="next-actions-stat-number">{stats.completed}</div>
              <div className="next-actions-stat-label">Completed</div>
            </div>
          </div>

          <div className="next-actions-stat-card overdue">
            <div className="next-actions-stat-icon">⏰</div>
            <div className="next-actions-stat-content">
              <div className="next-actions-stat-number">{stats.overdue}</div>
              <div className="next-actions-stat-label">Overdue</div>
            </div>
          </div>
        </div>
      )}

      {/* Filters - AGGIORNATI */}
      {!error && nextActions.length > 0 && (
        <div className="next-actions-filters">
          <div className="next-actions-filters-section">
            <label>🎯 Priority:</label>
            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              disabled={loading}
            >
              <option value="all">All Priorities</option>
              <option value="high">🔴 High Priority</option>
              <option value="medium">🟡 Medium Priority</option>
              <option value="low">🟢 Low Priority</option>
            </select>
          </div>

          <div className="next-actions-filters-section">
            <label>📅 Timing:</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              disabled={loading}
            >
              <option value="all">All Actions</option>
              <option value="overdue">⏰ Overdue</option>
              <option value="recent">🆕 Recent</option>
            </select>
          </div>

          <div className="next-actions-filters-section">
            <label>🔄 Action Status:</label>
            <select
              value={filterActionStatus}
              onChange={(e) => setFilterActionStatus(e.target.value)}
              disabled={loading}
            >
              <option value="all">All Statuses</option>
              <option value="pending">⏳ Pending</option>
              <option value="completed">✅ Completed</option>
              <option value="cancelled">❌ Cancelled</option>
            </select>
          </div>

          <div className="next-actions-filters-section">
            <label>🎨 Artwork Status:</label>
            <select
              value={filterArtworkStatus}
              onChange={(e) => setFilterArtworkStatus(e.target.value)}
              disabled={loading}
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="in-progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="obtained">Obtained</option>
            </select>
          </div>

          <div className="next-actions-filters-section">
            <label>🗂️ Sort by:</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              disabled={loading}
            >
              <option value="date">📅 Date</option>
              <option value="priority">🎯 Priority</option>
              <option value="status">🔄 Status</option>
              <option value="overdue">⏰ Days Overdue</option>
              <option value="artwork">🎨 Artwork</option>
            </select>
          </div>

          <div className="next-actions-filters-results">
            📋 Showing {filteredActions.length} of {nextActions.length} actions
          </div>
        </div>
      )}

      {/* Loading, Empty, e No Results States rimangono uguali */}
      {loading && !error && (
        <div className="next-actions-loading">
          <div className="next-actions-loading-spinner"></div>
          <p>Loading next actions from database...</p>
          <small>
            This may take a moment as we check all artwork operations...
          </small>
        </div>
      )}

      {/* Actions List - AGGIORNATA */}
      {!loading && !error && filteredActions.length > 0 && (
        <div className="next-actions-list">
          {filteredActions.map((action, index) => (
            <div
              key={`${action.artworkId}-${action.operationId}-${index}`}
              className={`next-action-card ${action.priority} ${
                action.daysOverdue && action.nextActionStatus === "pending"
                  ? "overdue"
                  : ""
              } ${action.nextActionStatus === "completed" ? "completed" : ""} ${
                action.nextActionStatus === "cancelled" ? "cancelled" : ""
              }`}
            >
              {/* Priority indicator */}
              <div
                className="next-action-priority-indicator"
                style={{ backgroundColor: getPriorityColor(action.priority) }}
              ></div>

              {/* Card Header */}
              <div className="next-action-header">
                <div className="next-action-header-left">
                  <div className="next-action-artwork-info">
                    <h3 className="next-action-artwork-title">
                      🎨 {action.artworkTitle}
                    </h3>
                    <p className="next-action-artwork-details">
                      👨‍🎨 {action.artworkArtist} • 📍 {action.artworkLocation}
                    </p>
                    <div className="next-action-artwork-status">
                      <span
                        className="next-action-artwork-status-badge"
                        style={{
                          backgroundColor: getArtworkStatusColor(
                            action.artworkStatus
                          ),
                        }}
                      >
                        {action.artworkStatus.replace("_", " ").toUpperCase()}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="next-action-header-right">
                  <div className="next-action-badges">
                    <span
                      className="next-action-priority-badge"
                      style={{
                        backgroundColor: getPriorityColor(action.priority),
                      }}
                    >
                      {getPriorityIcon(action.priority)}{" "}
                      {action.priority.toUpperCase()}
                    </span>

                    {/* ✅ NUOVO BADGE per lo status */}
                    <span
                      className="next-action-status-badge"
                      style={{
                        backgroundColor: getStatusColor(
                          action.nextActionStatus
                        ),
                      }}
                    >
                      {getStatusIcon(action.nextActionStatus)}{" "}
                      {action.nextActionStatus.toUpperCase()}
                    </span>

                    {action.daysOverdue &&
                      action.nextActionStatus === "pending" && (
                        <span className="next-action-overdue-badge">
                          ⏰ {action.daysOverdue} days overdue
                        </span>
                      )}

                    {/* ✅ NUOVO: Mostra data completamento */}
                    {action.nextActionStatus === "completed" &&
                      action.nextActionCompletedAt && (
                        <span className="next-action-completed-badge">
                          ✅ Completed{" "}
                          {formatDate(action.nextActionCompletedAt)}
                        </span>
                      )}
                  </div>
                </div>
              </div>

              {/* Action Content */}
              <div className="next-action-content">
                <div className="next-action-description">
                  <div className="next-action-description-header">
                    <span className="next-action-description-icon">⏭️</span>
                    <strong>Next Action:</strong>
                  </div>
                  <p
                    className={
                      action.nextActionStatus === "completed" ? "completed" : ""
                    }
                  >
                    {action.nextAction}
                  </p>
                </div>

                {/* ✅ NUOVO: Mostra note di completamento */}
                {action.nextActionCompletionNotes && (
                  <div className="next-action-completion-notes">
                    <div className="next-action-completion-notes-header">
                      <span className="next-action-completion-notes-icon">
                        📝
                      </span>
                      <strong>Completion Notes:</strong>
                    </div>
                    <p>{action.nextActionCompletionNotes}</p>
                  </div>
                )}

                <div className="next-action-metadata">
                  <div className="next-action-metadata-item">
                    <span className="next-action-metadata-icon">
                      {getOperationIcon(action.operationType)}
                    </span>
                    <span>
                      From {action.operationType.replace("_", " ")} operation
                    </span>
                  </div>

                  <div className="next-action-metadata-item">
                    <span className="next-action-metadata-icon">📅</span>
                    <span>Added on {formatDate(action.operationDate)}</span>
                  </div>

                  {action.operationNotes && (
                    <div className="next-action-metadata-item notes">
                      <span className="next-action-metadata-icon">📝</span>
                      <span>{action.operationNotes}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Card Actions - AGGIORNATE */}
              <div className="next-action-actions">
                <Link
                  to={`/artwork/${action.artworkId}`}
                  className="next-action-button view"
                >
                  👁️ View Artwork
                </Link>

                <Link
                  to={`/edit-artwork/${action.artworkId}`}
                  className="next-action-button edit"
                >
                  ✏️ Add Operation
                </Link>

                {/* ✅ NUOVI PULSANTI per gestire lo stato */}
                {action.nextActionStatus === "pending" && (
                  <>
                    <button
                      onClick={() =>
                        handleStatusChange(
                          action.artworkId,
                          action.operationId,
                          "completed"
                        )
                      }
                      className="next-action-button complete"
                      disabled={
                        updatingStatus ===
                        `${action.artworkId}-${action.operationId}`
                      }
                    >
                      {updatingStatus ===
                      `${action.artworkId}-${action.operationId}` ? (
                        <div className="next-actions-refresh-spinner"></div>
                      ) : (
                        <>✅ Mark Complete</>
                      )}
                    </button>

                    <button
                      onClick={() =>
                        handleStatusChange(
                          action.artworkId,
                          action.operationId,
                          "cancelled"
                        )
                      }
                      className="next-action-button cancel"
                      disabled={
                        updatingStatus ===
                        `${action.artworkId}-${action.operationId}`
                      }
                    >
                      ❌ Cancel
                    </button>
                  </>
                )}

                {(action.nextActionStatus === "completed" ||
                  action.nextActionStatus === "cancelled") && (
                  <button
                    onClick={() =>
                      handleStatusChange(
                        action.artworkId,
                        action.operationId,
                        "pending"
                      )
                    }
                    className="next-action-button reopen"
                    disabled={
                      updatingStatus ===
                      `${action.artworkId}-${action.operationId}`
                    }
                  >
                    {updatingStatus ===
                    `${action.artworkId}-${action.operationId}` ? (
                      <div className="next-actions-refresh-spinner"></div>
                    ) : (
                      <>🔄 Reopen</>
                    )}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty e No Results States rimangono uguali */}
      {!loading && !error && nextActions.length === 0 && (
        <div className="next-actions-empty">
          <div className="next-actions-empty-icon">🎉</div>
          <h3>No Pending Actions</h3>
          <p>Great! You don't have any next actions pending at the moment.</p>
          <div className="next-actions-empty-suggestions">
            <p>
              <strong>To create next actions:</strong>
            </p>
            <ul>
              <li>📝 Add operations to your artworks</li>
              <li>✅ Include "Next Action" in operation forms</li>
              <li>🔄 Follow up on your artwork progress</li>
            </ul>
          </div>
          <Link to="/artworks" className="next-actions-empty-button">
            📋 View Artworks
          </Link>
        </div>
      )}

      {!loading &&
        !error &&
        nextActions.length > 0 &&
        filteredActions.length === 0 && (
          <div className="next-actions-no-results">
            <div className="next-actions-no-results-icon">🔍</div>
            <h3>No Actions Match Your Filters</h3>
            <p>Try adjusting your filters to see more results.</p>
            <button
              onClick={() => {
                setFilterPriority("all");
                setFilterStatus("all");
                setFilterActionStatus("all");
                setFilterArtworkStatus("all");
              }}
              className="next-actions-no-results-button"
            >
              🧹 Clear All Filters
            </button>
          </div>
        )}
    </div>
  );
};

export default NextActionsView;
